'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { articlesApi } from '@/app/api/articles';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useToast } from '@/hooks/useToast';
import ArticleEditor from '../components/ArticleEditor';

import { ArticleMeta, ArticleMetadata } from '../utils/article-meta';
import { apiConfig } from '@/config/api';

// Use a simple loading indicator instead of importing a component that might not exist
// We'll create a simple inline loading component

export default function EditArticlePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [documentData, setDocumentData] = useState<{
    id: string;
    profileId?: string;
    title: string;
    content: string;
    status: string;
    category: string;
    tags: string[];
    suggestedTitles: string[];
  } | null>(null);

  // Fetch document data when component mounts
  useEffect(() => {
    const fetchDocumentData = async () => {
      setIsLoading(true);

      // Get document ID from URL
      const urlParams = new URLSearchParams(window.location.search);
      const documentId = urlParams.get('id');

      if (!documentId) {
        alert('Document ID not found');
        navigate('/articles');
        return;
      }

      try {
        // Fetch document metadata
        const document = await articlesApi.getArticle(documentId);

        // State Guard: redirect non-draft articles
        if (document.state !== 'draft') {
          // For published articles, redirect to the public view page
          if (document.state === 'published') {
            const publicUrl = `${apiConfig.wwwUrl}/articles/${document.slug || documentId}`;
            window.location.href = publicUrl;
            return;
          }

          // For other non-draft states (like archived), redirect to internal preview
          navigate(`/articles/preview?id=${documentId}`, {
            replace: true,
            state: { warning: 'Published or archived articles cannot be edited directly. Please restore to draft or clone to edit.' }
          });
          return;
        }

        // Fetch document content
        const contentBlob = await articlesApi.getArticleContent(documentId);
        const content = await contentBlob.text();

        // Fetch document tags
        const tags = await articlesApi.getArticleTags(documentId);

        // Extract suggested titles from metadata using ArticleMeta
        const suggestedTitles = ArticleMeta.getSuggestedTitles(document.meta);

        // Set document data
        setDocumentData({
          id: documentId,
          profileId: document.profile_id,
          title: document.meta?.title || '',
          content,
          status: document.state || 'draft',
          category: document.meta?.category || '',
          tags: tags || [],
          suggestedTitles
        });
      } catch (error) {
        console.error('Error fetching document:', error);
        alert('Failed to load document. Please try again.');
        navigate('/articles');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocumentData();
  }, [navigate]);

  const handleCancel = () => {
    // Check if we can/should close the window
    // 1. If we have an opener (opened via window.open)
    // 2. Or if history length is 1 (new tab/window with no history) - though this is flaky in some browsers

    // Better logic: If we have an opener, try to close.
    if (window.opener) {
      window.close();
      // Fallback if close failed (e.g. security restrictions)
      setTimeout(() => {
        if (!window.closed) {
          navigate('/articles');
        }
      }, 300);
    } else {
      // No opener means standard navigation (e.g. from Signup or Dashboard in same tab)
      navigate('/articles');
    }
  };

  const handleSave = async (articleData: {
    title: string;
    content: string;
    status: string;
    category: string;
    tags: string[];
    slug?: string;
    meta?: {
      suggested_titles?: string[];
      [key: string]: any;
    };
  }) => {
    if (isSubmitting) return;

    // Get document ID from URL
    const urlParams = new URLSearchParams(window.location.search);
    const documentId = urlParams.get('id');

    if (!documentId) {
      alert('Document ID not found');
      return;
    }

    // Require category and at least one tag when publishing
    if (articleData.status === 'published') {
      const hasCategory = !!(articleData.category && articleData.category.trim().length > 0);
      const hasTags = Array.isArray(articleData.tags) && articleData.tags.length > 0;

      if (!hasCategory || !hasTags) {
        alert('Please select a category and at least one tag before publishing this article.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Create content blob
      const contentBlob = new Blob([articleData.content], { type: 'text/markdown' });

      // Create file from blob
      const file = new File(
        [contentBlob],
        `${articleData.title.toLowerCase().replace(/\s+/g, '-')}.md`,
        { type: 'text/markdown' }
      );

      // Fetch the original document to get the full metadata object
      const document = await articlesApi.getArticle(documentId);
      const originalMeta = document.meta || {};
      // Merge and normalize metadata to preserve important fields
      const meta = ArticleMeta.mergeMetadata(originalMeta, articleData.meta || {});

      // Ensure the top-level fields are updated
      meta.title = articleData.title;
      meta.category = articleData.category;
      meta.filename = file.name;

      // Update the document metadata first
      await articlesApi.updateArticle(documentId, {
        meta,
        state: articleData.status,
      });

      // Then, update the content separately
      await articlesApi.updateArticleContent(documentId, articleData.content);

      // Atomically update tags
      await articlesApi.setArticleTags(documentId, articleData.tags);

      // If publishing, call the publish API
      if (articleData.status === 'published') {
        await articlesApi.publishArticle(documentId, articleData.slug);
      }

      handleCancel();
    } catch (error) {
      console.error('Error updating article:', error);
      alert('Failed to update article. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Simple loading component
  const LoadingIndicator2 = () => (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      <span className="ml-3 text-lg">Loading document...</span>
    </div>
  );

  const LoadingIndicator = () => (
    <div className={`flex flex-col items-center justify-center ${isMobile ? 'fixed inset-0 z-[100] bg-white' : 'h-96'}`}>
      <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
      <p className="mt-4 text-gray-800 font-medium tracking-wide">Loading document...</p>
    </div>
  );

  return (

    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isLoading ? (
          <LoadingIndicator />
        ) : documentData ? (
          <ArticleEditor
            mode="edit"
            onSave={handleSave}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            initialTitle={documentData.title}
            initialContent={documentData.content}
            initialStatus={documentData.status}
            initialCategory={documentData.category}
            initialTags={documentData.tags}
            initialSuggestedTitles={documentData.suggestedTitles}
            initialProfileId={documentData.profileId}
            defaultToAgentMode={true}
          />
        ) : (
          <div className="text-center py-10">
            <p className="text-red-500">Failed to load document. Please try again.</p>
            <button
              onClick={() => navigate('/articles')}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Return to Articles
            </button>
          </div>
        )}
      </div>
    </div>

  );
}
